import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { lotesAPI } from '../lib/api';
import { formatNumber } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft,
  FilePdf,
  FloppyDisk,
  Bird,
  Thermometer,
  Scales,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Base days array - will be reordered based on lote start date
const BASE_DAYS = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
const BASE_DAYS_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

// Helper function to get ordered days based on start date
const getOrderedDays = (fechaInicio) => {
  if (!fechaInicio) {
    // Default to Monday start if no date
    return {
      days: ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'],
      labels: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
    };
  }
  
  // Parse the date and get the day of week (0=Sunday, 1=Monday, etc.)
  const date = new Date(fechaInicio);
  const startDayIndex = date.getUTCDay(); // 0-6
  
  // Reorder days starting from the start day
  const days = [];
  const labels = [];
  for (let i = 0; i < 7; i++) {
    const idx = (startDayIndex + i) % 7;
    days.push(BASE_DAYS[idx]);
    labels.push(BASE_DAYS_LABELS[idx]);
  }
  
  return { days, labels };
};

const TEMP_ESTANDAR = {
  1: "30-32°C",
  2: "28-30°C",
  3: "26-28°C",
  4: "24-26°C",
  5: "22-24°C",
  6: "20-22°C",
  7: "20-22°C",
  8: "20-22°C"
};

const CONV_ESTANDAR = {
  1: 0.856,
  2: 1.06,
  3: 1.26,
  4: 1.45,
  5: 1.61,
  6: 1.76,
  7: 1.9,
  8: 2.0
};

const PESO_ESPERADO = {
  1: "0.40-0.45",
  2: "0.95-1.20",
  3: "1.60-2.00",
  4: "2.80-3.60",
  5: "4.00-4.50",
  6: "4.80-5.90",
  7: "6.00-7.30",
  8: "7.00-8.50"
};

export default function FarmRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lote, setLote] = useState(null);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeWeek, setActiveWeek] = useState('1');
  const [formData, setFormData] = useState({
    proyecto: '',
    nave_numero: '',
    peso_inicio: 0.09,
  });
  const [weeklyData, setWeeklyData] = useState([]);
  const [orderedDays, setOrderedDays] = useState({ 
    days: ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'],
    labels: ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const loteRes = await lotesAPI.getOne(id);
      setLote(loteRes.data);
      
      // Set ordered days based on lote start date
      const days = getOrderedDays(loteRes.data.fecha_inicio);
      setOrderedDays(days);
      
      const token = localStorage.getItem('token');
      try {
        const recordRes = await axios.get(`${API_BASE}/farm-records/lote/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRecord(recordRes.data);
        setFormData({
          proyecto: recordRes.data.proyecto || '',
          nave_numero: recordRes.data.nave_numero || '',
          peso_inicio: recordRes.data.peso_inicio || 0.09,
        });
        setWeeklyData(recordRes.data.weekly_records);
      } catch (err) {
        if (err.response?.status === 404) {
          // No record exists, initialize empty one
          initializeWeeklyData(days.days);
        } else {
          throw err;
        }
      }
    } catch (error) {
      toast.error('Error al cargar datos');
      navigate('/lotes');
    } finally {
      setLoading(false);
    }
  };

  const initializeWeeklyData = (daysArray = null) => {
    const weeks = [];
    // Use provided days or current orderedDays
    const daysToUse = daysArray || orderedDays.days;
    
    for (let i = 1; i <= 8; i++) {
      // Create object with all 7 days initialized to 0
      const dayObj = {};
      daysToUse.forEach(day => { dayObj[day] = 0; });
      
      weeks.push({
        semana: i,
        alimento: { ...dayObj },
        mortalidad: { ...dayObj },
        descarte: { ...dayObj },
        peso_actual: null,
        temp_estandar: TEMP_ESTANDAR[i],
        conv_estandar: CONV_ESTANDAR[i],
        peso_esperado: PESO_ESPERADO[i],
      });
    }
    setWeeklyData(weeks);
  };

  const handleCreateRecord = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      // First create the record
      const createRes = await axios.post(`${API_BASE}/farm-records`, {
        lote_id: id,
        proyecto: formData.proyecto || lote.nombre,
        nave_numero: formData.nave_numero,
        peso_inicio: formData.peso_inicio,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Then save the weekly data
      await axios.put(`${API_BASE}/farm-records/lote/${id}`, weeklyData.map(w => ({
        semana: w.semana,
        alimento: w.alimento,
        mortalidad: w.mortalidad,
        descarte: w.descarte,
        peso_actual: w.peso_actual,
      })), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Registro de granja guardado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear registro');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWeek = async () => {
    if (!record) {
      await handleCreateRecord();
      return;
    }
    
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API_BASE}/farm-records/lote/${id}`, weeklyData.map(w => ({
        semana: w.semana,
        alimento: w.alimento,
        mortalidad: w.mortalidad,
        descarte: w.descarte,
        peso_actual: w.peso_actual,
      })), {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Datos guardados correctamente');
      loadData();
    } catch (error) {
      toast.error('Error al guardar datos');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      toast.loading('Generando PDF...');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/reports/farm-record/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `record_granja_${lote.nombre}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('PDF descargado');
    } catch (error) {
      toast.dismiss();
      toast.error('Error al generar PDF');
    }
  };

  const updateWeekValue = (weekIdx, type, day, value) => {
    const newData = [...weeklyData];
    newData[weekIdx][type][day] = parseFloat(value) || 0;
    setWeeklyData(newData);
  };

  const updatePesoActual = (weekIdx, value) => {
    const newData = [...weeklyData];
    newData[weekIdx].peso_actual = parseFloat(value) || null;
    setWeeklyData(newData);
  };

  const calculateTotals = (weekIdx, type) => {
    if (!weeklyData[weekIdx]) return { semanal: 0, acumulado: 0 };
    
    const week = weeklyData[weekIdx];
    // Sum all days in this week for the given type
    const semanal = orderedDays.days.reduce((sum, day) => sum + (week[type]?.[day] || 0), 0);
    
    // Calculate accumulated from week 0 to weekIdx (inclusive)
    let acumulado = 0;
    for (let i = 0; i <= weekIdx; i++) {
      const w = weeklyData[i];
      if (w && w[type]) {
        acumulado += orderedDays.days.reduce((sum, day) => sum + (w[type]?.[day] || 0), 0);
      }
    }
    
    return { semanal, acumulado };
  };

  // Calculate grand total across ALL 8 weeks
  const calculateGrandTotal = (type) => {
    let total = 0;
    for (let i = 0; i < weeklyData.length; i++) {
      const week = weeklyData[i];
      if (week && week[type]) {
        total += orderedDays.days.reduce((sum, day) => sum + (week[type]?.[day] || 0), 0);
      }
    }
    return total;
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  const currentWeekIdx = parseInt(activeWeek) - 1;
  const currentWeek = weeklyData[currentWeekIdx] || {};

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="farm-record">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <Link
              to={`/lotes/${id}`}
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
            >
              <ArrowLeft size={14} />
              Volver al Lote
            </Link>
            <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Record de Granja
            </h1>
            <p className="text-muted-foreground">
              {lote?.nombre} - Control diario de alimento, mortalidad y peso
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleDownloadPDF}
              disabled={!record}
              data-testid="download-farm-pdf"
            >
              <FilePdf size={18} className="mr-2" />
              Descargar PDF
            </Button>
            <Button
              className="bg-[#E73425] hover:bg-[#D81C23]"
              onClick={handleSaveWeek}
              disabled={saving}
              data-testid="save-record"
            >
              {saving ? (
                <div className="spinner h-5 w-5" />
              ) : (
                <>
                  <FloppyDisk size={18} className="mr-2" />
                  Guardar
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Info Header */}
        <Card className="border-border/60 bg-gradient-to-r from-red-50 to-orange-50">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Fecha Inicio</p>
                <p className="font-semibold">{lote?.fecha_inicio?.slice(0, 10)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Proyecto</p>
                <Input
                  value={formData.proyecto}
                  onChange={(e) => setFormData({ ...formData, proyecto: e.target.value })}
                  placeholder={lote?.nombre}
                  className="h-8 mt-1"
                  data-testid="proyecto-input"
                />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Nave #</p>
                <Input
                  value={formData.nave_numero}
                  onChange={(e) => setFormData({ ...formData, nave_numero: e.target.value })}
                  placeholder="Número"
                  className="h-8 mt-1"
                  data-testid="nave-input"
                />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Pollos Inicial</p>
                <p className="font-semibold text-lg">{formatNumber(lote?.cantidad_pollitos)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Pollos Actuales</p>
                <p className="font-semibold text-lg text-emerald-600" data-testid="pollos-actuales-header">
                  {formatNumber((lote?.cantidad_pollitos || 0) - calculateGrandTotal('mortalidad') - calculateGrandTotal('descarte'))}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Peso Inicio (lb)</p>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.peso_inicio}
                  onChange={(e) => setFormData({ ...formData, peso_inicio: parseFloat(e.target.value) })}
                  className="h-8 mt-1"
                  data-testid="peso-inicio-input"
                />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Tipo</p>
                <p className="font-semibold">Pollos Cobb-500</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Week Selector */}
        <Tabs value={activeWeek} onValueChange={setActiveWeek}>
          <TabsList className="grid w-full grid-cols-8">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((week) => (
              <TabsTrigger key={week} value={week.toString()} data-testid={`week-tab-${week}`}>
                Sem {week}
              </TabsTrigger>
            ))}
          </TabsList>

          {[1, 2, 3, 4, 5, 6, 7, 8].map((week) => {
            const weekIdx = week - 1;
            const weekData = weeklyData[weekIdx] || {};
            const alimentoTotals = calculateTotals(weekIdx, 'alimento');
            const mortalidadTotals = calculateTotals(weekIdx, 'mortalidad');
            const descarteTotals = calculateTotals(weekIdx, 'descarte');

            return (
              <TabsContent key={week} value={week.toString()} className="space-y-6">
                {/* Standards Info */}
                <div className="flex flex-wrap gap-4 items-center justify-center py-2 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Thermometer size={20} className="text-orange-500" />
                    <span className="text-sm">
                      <strong>Temp. Ideal:</strong> {TEMP_ESTANDAR[week]}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Bird size={20} className="text-[#E73425]" />
                    <span className="text-sm">
                      <strong>Conv. Estándar:</strong> {CONV_ESTANDAR[week]}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Scales size={20} className="text-emerald-500" />
                    <span className="text-sm">
                      <strong>Peso Esperado:</strong> {PESO_ESPERADO[week]} lb
                    </span>
                  </div>
                </div>

                {/* ALIMENTO TABLE */}
                <Card className="border-border/60">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500" />
                      Consumo de Alimento (QQS) - Semana {week}
                    </CardTitle>
                    <CardDescription>
                      Registre el alimento diario en sacos de 100 lbs (anote por unidad)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-amber-50">
                            {orderedDays.labels.map((day, idx) => (
                              <th key={idx} className="p-2 text-center font-semibold">{day}</th>
                            ))}
                            <th className="p-2 text-center font-semibold bg-amber-100">Semanal</th>
                            <th className="p-2 text-center font-semibold bg-amber-200">Acumulado</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {orderedDays.days.map((day, idx) => (
                              <td key={idx} className="p-1">
                                <Input
                                  type="number"
                                  step="0.5"
                                  min="0"
                                  value={weekData.alimento?.[day] !== undefined && weekData.alimento?.[day] !== null ? weekData.alimento[day] : ''}
                                  onChange={(e) => updateWeekValue(weekIdx, 'alimento', day, e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                  className="h-10 text-center"
                                  placeholder="0"
                                  data-testid={`alimento-${day}-${week}`}
                                />
                              </td>
                            ))}
                            <td className="p-2 text-center font-bold bg-amber-50">
                              {alimentoTotals.semanal.toFixed(1)}
                            </td>
                            <td className="p-2 text-center font-bold bg-amber-100">
                              {alimentoTotals.acumulado.toFixed(1)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* MORTALIDAD TABLE */}
                <Card className="border-border/60">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      Mortalidad - Semana {week}
                    </CardTitle>
                    <CardDescription>
                      Número de pollos muertos por día
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-red-50">
                            {orderedDays.labels.map((day, idx) => (
                              <th key={idx} className="p-2 text-center font-semibold">{day}</th>
                            ))}
                            <th className="p-2 text-center font-semibold bg-red-100">Semanal</th>
                            <th className="p-2 text-center font-semibold bg-red-200">Acumulado</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {orderedDays.days.map((day, idx) => (
                              <td key={idx} className="p-1">
                                <Input
                                  type="number"
                                  min="0"
                                  value={weekData.mortalidad?.[day] !== undefined && weekData.mortalidad?.[day] !== null ? weekData.mortalidad[day] : ''}
                                  onChange={(e) => updateWeekValue(weekIdx, 'mortalidad', day, e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                  className="h-10 text-center"
                                  placeholder="0"
                                  data-testid={`mortalidad-${day}-${week}`}
                                />
                              </td>
                            ))}
                            <td className="p-2 text-center font-bold bg-red-50">
                              {mortalidadTotals.semanal}
                            </td>
                            <td className="p-2 text-center font-bold bg-red-100">
                              {mortalidadTotals.acumulado}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Peso del pollo */}
                    <div className="mt-4 flex items-center gap-4 p-4 bg-emerald-50 rounded-lg">
                      <div className="flex-1">
                        <Label className="text-sm font-medium">Peso del Pollo (Libras)</Label>
                        <p className="text-xs text-muted-foreground">Esperado: {PESO_ESPERADO[week]} lb</p>
                      </div>
                      <div className="w-32">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={weekData.peso_actual !== undefined && weekData.peso_actual !== null ? weekData.peso_actual : ''}
                          onChange={(e) => updatePesoActual(weekIdx, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          placeholder="Actual"
                          className="text-center"
                          data-testid={`peso-actual-${week}`}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* DESCARTE TABLE */}
                <Card className="border-border/60">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-slate-500" />
                      Descarte - Semana {week}
                    </CardTitle>
                    <CardDescription>
                      Pollos descartados por día
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            {orderedDays.labels.map((day, idx) => (
                              <th key={idx} className="p-2 text-center font-semibold">{day}</th>
                            ))}
                            <th className="p-2 text-center font-semibold bg-slate-100">Semanal</th>
                            <th className="p-2 text-center font-semibold bg-slate-200">Acumulado</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {orderedDays.days.map((day, idx) => (
                              <td key={idx} className="p-1">
                                <Input
                                  type="number"
                                  min="0"
                                  value={weekData.descarte?.[day] !== undefined && weekData.descarte?.[day] !== null ? weekData.descarte[day] : ''}
                                  onChange={(e) => updateWeekValue(weekIdx, 'descarte', day, e.target.value)}
                                  onFocus={(e) => e.target.select()}
                                  className="h-10 text-center"
                                  placeholder="0"
                                  data-testid={`descarte-${day}-${week}`}
                                />
                              </td>
                            ))}
                            <td className="p-2 text-center font-bold bg-slate-50">
                              {descarteTotals.semanal}
                            </td>
                            <td className="p-2 text-center font-bold bg-slate-100">
                              {descarteTotals.acumulado}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>

        {/* Resumen General */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle>Resumen General</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Alimento Total</p>
                <p className="text-3xl font-bold text-amber-600" style={{ fontFamily: 'JetBrains Mono' }} data-testid="alimento-total">
                  {calculateGrandTotal('alimento').toFixed(1)} QQS
                </p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Mortalidad Total</p>
                <p className="text-3xl font-bold text-red-600" style={{ fontFamily: 'JetBrains Mono' }} data-testid="mortalidad-total">
                  {calculateGrandTotal('mortalidad')}
                </p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Descarte Total</p>
                <p className="text-3xl font-bold text-slate-600" style={{ fontFamily: 'JetBrains Mono' }} data-testid="descarte-total">
                  {calculateGrandTotal('descarte')}
                </p>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-lg">
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Pollos Actuales</p>
                <p className="text-3xl font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono' }} data-testid="pollos-actuales-resumen">
                  {formatNumber((lote?.cantidad_pollitos || 0) - calculateGrandTotal('mortalidad') - calculateGrandTotal('descarte'))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
